import { RouterProvider, createRouter } from '@tanstack/react-router'
import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'

// Import the generated route tree

import { routeTree } from './routeTree.gen'

import reportWebVitals from './reportWebVitals.ts'
import { apiRestoreUserOrgProfile } from './api/user/profile.ts'
import * as SentryClient from './sentryClient'
import type { UserOrgProfile } from './api/user/profile.ts'
import './styles.css'

// Capture any uncaught errors thrown before the Sentry SDK finishes loading —
// replayed after initSentry() resolves. Without this, errors in the first
// ~100ms of page load would be silently dropped.
const earlyErrorHandler = (e: ErrorEvent) => {
  // Cross-origin scripts without CORS surface as a stackless "Script error." — nothing useful to report.
  if (e.error == null && e.message === 'Script error.') return
  SentryClient.captureException(e.error ?? new Error(e.message))
}
const earlyRejectionHandler = (e: PromiseRejectionEvent) => SentryClient.captureException(e.reason)
if (import.meta.env.PROD) {
  window.addEventListener('error', earlyErrorHandler)
  window.addEventListener('unhandledrejection', earlyRejectionHandler)
}

export interface MyRouterContext {
  user: {
    isAuthenticated: boolean
    userOrgProfile?: UserOrgProfile
  }
}

// Attempt to restore an existing session before the router renders any route.
// Bounded by a 5s timeout so the app always renders even if the backend is slow/unreachable.
// Retries once on transient failure before falling back to unauthenticated.
const RESTORE_SESSION_TIMEOUT_MS = 5000

async function restoreSession(): Promise<MyRouterContext> {
  const attempt = () =>
    Promise.race([
      apiRestoreUserOrgProfile(),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Session restore timeout')), RESTORE_SESSION_TIMEOUT_MS)
      )
    ])

  for (let tries = 0; tries < 2; tries++) {
    try {
      const userOrgProfile = await attempt()
      if (userOrgProfile === undefined) return { user: { isAuthenticated: false } }
      return { user: { isAuthenticated: true, userOrgProfile } }
    } catch {
      // First failure — retry once; second failure — give up
    }
  }
  return { user: { isAuthenticated: false } }
}

const initialContext = await restoreSession()

if (initialContext.user.userOrgProfile) {
  const { email, first_name, surname } = initialContext.user.userOrgProfile
  SentryClient.setUser({ email, username: `${first_name} ${surname}`.trim() })
}

// Create a new router instance
const router = createRouter({
  basepath: '/app',
  routeTree,
  context: initialContext,
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

function App() {
  return <RouterProvider router={router} />
}

// Render the app.
//
// Vite HMR can re-execute this entry module without a full page reload. React's render is async, so
// the previous `!rootElement.innerHTML` guard raced: #app could still be empty when the re-run
// checked it, so createRoot ran a second time on the same container. React warns ("createRoot() on a
// container that has already been passed to createRoot()") and mounts a second, duplicate app tree —
// two stacked sidebars/composers that only clear on a full restart. Caching the root on window makes
// creation idempotent: a re-run reuses the existing root and just re-renders.
const rootElement = document.getElementById('app')
if (rootElement) {
  const globalWithRoot = window as typeof window & { __svReactRoot?: ReactDOM.Root }
  const root = globalWithRoot.__svReactRoot ?? (globalWithRoot.__svReactRoot = ReactDOM.createRoot(rootElement))
  root.render(
    <StrictMode>
      <App />
    </StrictMode>
  )
}

// Load the Sentry SDK after first paint. Errors thrown before init are
// captured by the early window handlers above and replayed once init resolves.
if (import.meta.env.PROD) {
  const startSentry = () => {
    void SentryClient.initSentry((Sentry) => ({
      dsn: 'https://aec71405981a82425ce17b10297ab857@o4507264015335424.ingest.us.sentry.io/4508697087967232',
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({ maskAllText: true, blockAllMedia: false }),
        Sentry.feedbackIntegration({
          autoInject: false,
          showBranding: false,
          enableScreenshot: true,
          formTitle: 'Give Feedback',
          messagePlaceholder: 'Please provide as much detail as possible',
          submitButtonLabel: 'Send Feedback',
          showName: true,
          showEmail: true,
          themeLight: { border: '1.5px solid #e1e8e6' },
          themeDark: { border: '1.5px solid #1e2624' }
        })
      ],
      tracesSampleRate: 1.0,
      tracePropagationTargets: [/^\/v2\/app/, /^\/api/],
      replaysSessionSampleRate: 1.0,
      replaysOnErrorSampleRate: 1.0
    }))
      .catch((err: unknown) => console.error('Sentry init failed', err))
      .finally(() => {
        window.removeEventListener('error', earlyErrorHandler)
        window.removeEventListener('unhandledrejection', earlyRejectionHandler)
      })
  }
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(startSentry)
  } else {
    setTimeout(startSentry, 0)
  }
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
